/**
 * HOF-216 AC 2 — the right-click context menu on a matched line edge.
 *
 * The design-system ``ContextMenu`` molecule is a kebab-triggered dropdown: it
 * owns its own trigger button and its own open state, so it cannot be anchored
 * at a pointer position over an SVG edge. This is the pointer-anchored
 * equivalent and deliberately minimal — ONE action, which is the same
 * ``onUnlink`` callback the on-edge control fires, so the two affordances take
 * the identical code path rather than two that can drift.
 */
import { useEffect, useRef } from 'react';

import { UNLINK_ACTION_LABEL } from '../unlinkConfirmation';
import './EdgeUnlinkMenu.css';

export interface EdgeUnlinkMenuProps {
  /** Container-relative pointer position the right-click landed at. */
  x: number;
  y: number;
  onUnlink: () => void;
  onClose: () => void;
}

export function EdgeUnlinkMenu({ x, y, onUnlink, onClose }: EdgeUnlinkMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const handleOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleOutside);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      role="menu"
      className="nodrag nopan gc-edge-unlink-menu"
      style={{ left: `${x}px`, top: `${y}px` }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <button
        type="button"
        role="menuitem"
        className="gc-edge-unlink-menu__item"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onUnlink();
          onClose();
        }}
      >
        <span className="gc-edge-unlink-menu__label">{UNLINK_ACTION_LABEL}</span>
        <span className="gc-edge-unlink-menu__icon" aria-hidden="true">
          ✕
        </span>
      </button>
    </div>
  );
}

export default EdgeUnlinkMenu;
